// CompareSheet — two organisations, cell by cell (BUILD-BRIEF-R3 §4).
//
// A `.sheet.sheet--edge`, the blocks as `.fgroup`, one row per cell with both values side by
// side, ▪ held / ◇ proposed / an "estimated" `.tag--ghost` / "not collected" in `fv--unknown`,
// and a click on a value opening its `blockquote.ev`.
//
// THE ROW THAT MATTERS IS THE ONE THAT DIFFERS, and the one that is EMPTY ON BOTH SIDES is
// still worth showing: "neither of these two has ever been asked about their auditor" is a fact
// about our coverage, not a gap to hide.
//
// THERE ARE TWO KINDS OF DIFFERENCE AND THE HEAD COUNTS THEM APART. `CompareCell.differs` is a
// disagreement — both sides collected the cell and answered differently. A cell one side holds
// and the other has never been asked about is not a disagreement, but it is not sameness either,
// and a single "0 cells differ" over a pair where one raster is full and the other is empty
// would read as "these two are identical", which is the one thing the sheet must never say. So
// the head reads TWO figures — `n cells differ where both are collected` and `m on one side
// only` — and the filter narrows to both kinds, because a value only one side holds is exactly
// what an operator opening a comparison is looking for. Only a cell empty on BOTH sides is
// counted in neither; that one is a fact about our coverage, and the head above says so by
// leaving it out of both figures rather than by hiding the row.
//
// THE DRESS IS NOT REINVENTED HERE. Every value renders through `CellValue` in ProfileZone, so
// a cell cannot look one way on the Organisation brief and another way in the comparison.
import { useMemo, useState } from 'react';
import EmptyState from './EmptyState.js';
import { CellValue, EvidenceSrc } from './ProfileZone.js';
import { Ev, Fig, Ref, Sep, Sheet } from './PageKit.js';
import { useLocale, useT } from '../i18n/index.js';
import { RASTER, labelFor, type RasterCell, type RasterLang } from '../../shared/raster.v1.js';
import type { CompareCell, CompareResponse, CompareSide, ProfileCellValue } from '../../shared/types.js';
import './CompareSheet.de.js';
import './CompareSheet.fr.js';

export interface CompareSheetProps {
  /** Null while nothing has answered — which is not "the two are identical". */
  comparison?: CompareResponse | null;
  error?: string | null;
  loading?: boolean;
  onClose?: () => void;
  onRetry?: () => void;
}

/** label · A · B — the same three tracks in the head and in every row. */
const GRID = { gridTemplateColumns: 'minmax(140px, 200px) 1fr 1fr' } as const;

const CELL_BY_KEY = new Map<string, RasterCell>();
for (const block of RASTER) {
  for (const cell of block.cells) CELL_BY_KEY.set(`profile.${block.key}.${cell.key}`, cell);
}

export default function CompareSheet({ comparison, error, loading, onClose, onRetry }: CompareSheetProps) {
  const t = useT();
  const { lang, fmtNum, fmtDate } = useLocale();
  const [onlyDiffs, setOnlyDiffs] = useState(false);
  const [openCell, setOpenCell] = useState<string | null>(null);

  // A cell exactly one side collected. Not a disagreement — nobody asked the other side — but
  // never sameness, so it is counted and shown, and never folded into `differs`.
  const oneSided = (c: CompareCell): boolean => !!c.a?.value !== !!c.b?.value;
  /** A row the filter keeps: a disagreement, or a value only one side holds. */
  const notable = (c: CompareCell): boolean => c.differs || oneSided(c);

  const { differing, single } = useMemo(() => {
    let differing = 0;
    let single = 0;
    for (const b of comparison?.blocks ?? []) {
      for (const c of b.cells) {
        if (c.differs) differing += 1;
        else if (oneSided(c)) single += 1;
      }
    }
    return { differing, single };
  }, [comparison]);

  if (error) {
    return (
      <EmptyState
        kind="error"
        title={t('The comparison did not answer')}
        detail={<>GET /api/market/compare — {error}</>}
        onRetry={onRetry}
      />
    );
  }
  if (loading || !comparison) {
    return (
      <Sheet edge pad>
        <p className="zone-empty">{loading ? t('Loading…') : t('Pick two organisations in the grid to compare their rasters.')}</p>
      </Sheet>
    );
  }

  return (
    <Sheet edge pad>
      {/* the head: who is being compared, how much each one holds, and the two verbs */}
      <div className="fact" style={GRID}>
        <span className="fk">
          {/* Two figures, never one: a disagreement and a one-sided value are different facts,
              and "0 cells differ" alone over an empty raster would claim they are the same. */}
          <Fig>{differing}</Fig> {t('cells differ where both are collected')}
          <Sep />
          <Fig>{single}</Fig> {t('on one side only')}
          <Sep />
          <button
            type="button"
            className={`btn btn-sm btn-ghost${onlyDiffs ? ' active' : ''}`}
            aria-pressed={onlyDiffs}
            disabled={differing + single === 0}
            onClick={() => setOnlyDiffs((v) => !v)}
          >
            {onlyDiffs ? t('Show every cell') : t('Only what differs')}
          </button>
          {onClose && (
            <>
              {' '}
              <button type="button" className="btn btn-sm btn-ghost" onClick={onClose}>{t('Close')}</button>
            </>
          )}
        </span>
        <SideHead side={comparison.a} />
        <SideHead side={comparison.b} />
      </div>

      {comparison.blocks.map((block) => {
        const rows = onlyDiffs ? block.cells.filter(notable) : block.cells;
        if (rows.length === 0) return null;
        return (
          <div className="fgroup" key={block.block}>
            <div className="fgroup-h u-label">{labelFor(lang as RasterLang, block.block)}</div>
            {rows.map((row) => (
              <CompareRow
                key={row.key}
                row={row}
                lang={lang as RasterLang}
                fmtNum={fmtNum}
                fmtDate={fmtDate}
                open={openCell === row.key}
                onToggle={() => setOpenCell((k) => (k === row.key ? null : row.key))}
              />
            ))}
          </div>
        );
      })}

      {onlyDiffs && differing + single === 0 && (
        <p className="zone-empty">{t('Nothing differs between these two — every cell either matches or is uncollected on both sides.')}</p>
      )}
    </Sheet>
  );
}

function SideHead({ side }: { side: CompareSide }) {
  const t = useT();
  return (
    <span>
      <span className="w">{side.name}</span> <Ref>{side.ref}</Ref>
      <br />
      <span className="d">
        <Fig unit={`/ ${side.coverage.defined}`}>{side.coverage.filled}</Fig> {t('cells')}
        <Sep />
        {side.score
          ? <>{t(side.score.tier)} · <Fig>{side.score.composite}</Fig></>
          : <span className="fv--unknown">{t('not scored')}</span>}
      </span>
    </span>
  );
}

function CompareRow({ row, lang, fmtNum, fmtDate, open, onToggle }: {
  row: CompareCell;
  lang: RasterLang;
  fmtNum: (n: number | string | null | undefined) => string;
  fmtDate: (iso: string | null | undefined) => string;
  open: boolean;
  onToggle: () => void;
}) {
  const t = useT();
  const cell = CELL_BY_KEY.get(row.key);
  if (!cell) return null;
  const evidence = [row.a, row.b].filter((v): v is ProfileCellValue => !!v && (!!v.quote || !!v.evidence_ref));
  return (
    <>
      <div className={`fact${row.differs ? ' row--new' : ''}`} style={GRID} title={cell.sources_hint}>
        <span className="fk">{labelFor(lang, row.block, row.cell)}</span>
        <span>
          <ValueButton value={row.a} cell={cell} blockKey={row.block} lang={lang} fmtNum={fmtNum} onToggle={onToggle} />
        </span>
        <span>
          <ValueButton value={row.b} cell={cell} blockKey={row.block} lang={lang} fmtNum={fmtNum} onToggle={onToggle} />
        </span>
      </div>
      {open && (
        <div className="fact" style={{ gridTemplateColumns: '1fr' }}>
          <span>
            {evidence.length === 0
              ? <span className="zone-empty">{t('Neither side keeps a verbatim passage for this cell.')}</span>
              : evidence.map((v, i) => (
                  <Ev key={i} src={<EvidenceSrc evidenceKind={v.evidence_kind} evidenceRef={v.evidence_ref} fmtDate={fmtDate} asOf={v.as_of ?? null} />}>
                    {v.quote ?? t('The evidence names a source but keeps no verbatim passage.')}
                  </Ev>
                ))}
          </span>
        </div>
      )}
    </>
  );
}

/** A value that opens its own evidence. A cell nobody filled has nothing to open. */
function ValueButton({ value, cell, blockKey, lang, fmtNum, onToggle }: {
  value: ProfileCellValue | null;
  cell: RasterCell;
  blockKey: string;
  lang: RasterLang;
  fmtNum: (n: number | string | null | undefined) => string;
  onToggle: () => void;
}) {
  const t = useT();
  const rendered = <CellValue value={value} cell={cell} blockKey={blockKey} lang={lang} fmtNum={fmtNum} />;
  const openable = !!(value?.quote || value?.evidence_ref);
  if (!openable) return rendered;
  return (
    <button
      type="button"
      className="lnk"
      style={{ textAlign: 'left' }}
      title={t('Show the verbatim evidence for this value')}
      onClick={onToggle}
    >
      {rendered}
    </button>
  );
}
